import { useRef, type KeyboardEvent } from 'react';
import { BROWSER_TABS as TABS, panelId, tabId, type IteroBrowserTab } from './browserTabs';

interface BrowserSegmentsProps {
    baseId: string;
    activeTab: IteroBrowserTab;
    counts: Partial<Record<IteroBrowserTab, number | null>>;
    onChange: (tab: IteroBrowserTab) => void;
}

// A segmented control with the ARIA tabs pattern: arrow keys move between segments.
export function BrowserSegments({ baseId, activeTab, counts, onChange }: BrowserSegmentsProps) {
    const buttonRefs = useRef<Partial<Record<IteroBrowserTab, HTMLButtonElement | null>>>({});

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const index = TABS.findIndex((tab) => tab.id === activeTab);
        const step = event.key === 'ArrowRight' ? 1 : -1;
        const next = TABS[(index + step + TABS.length) % TABS.length].id;
        onChange(next);
        buttonRefs.current[next]?.focus();
    };

    return (
        <div
            role="tablist"
            aria-label="iTero data"
            onKeyDown={handleKeyDown}
            className="inline-flex shrink-0 items-center rounded-[9px] bg-gray-100 p-[3px]"
        >
            {TABS.map((tab) => {
                const isActive = tab.id === activeTab;
                const count = counts[tab.id];
                return (
                    <button
                        key={tab.id}
                        ref={(node) => {
                            buttonRefs.current[tab.id] = node;
                        }}
                        id={tabId(baseId, tab.id)}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        aria-controls={panelId(baseId, tab.id)}
                        tabIndex={isActive ? 0 : -1}
                        onClick={() => onChange(tab.id)}
                        className={`inline-flex h-[30px] items-center gap-1.5 rounded-[7px] px-3.5 text-[13px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 ${
                            isActive
                                ? 'bg-white font-semibold text-gray-900 shadow-sm'
                                : 'font-medium text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        {tab.label}
                        {count != null && (
                            <span className="text-[12px] font-medium tabular-nums text-gray-400">
                                {count.toLocaleString('en-US')}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}
